import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Circle, Clock, Target, Layers, Code2, Map, PenTool, Bot, LineChart, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PHASE_ICONS = [Search, Code2, PenTool, Map, Bot, LineChart];
const PHASE_COLORS = ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#06b6d4', '#ec4899'];

export default function ClientLocalSEO({ client }) {
  const { t } = useTranslation('dashboard');
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedPhase, setExpandedPhase] = useState(null);

  useEffect(() => {
    if (!client?.slug) return;
    fetch(`${API}/api/client/local-seo/campaign/${client.slug}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setCampaign(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [client]);

  if (loading) return <div className="text-sm text-gray-500 p-8 text-center">{t('localSeo.loadingCampaign')}</div>;
  if (!campaign) return (
    <div className="text-center py-16 text-gray-500" data-testid="no-campaign">
      <Target className="w-12 h-12 mx-auto mb-3 opacity-30" />
      <p className="text-lg font-medium text-gray-400">{t('localSeo.noCampaignTitle')}</p>
      <p className="text-sm mt-1">{t('localSeo.noCampaignSub')}</p>
    </div>
  );

  const allTasks = campaign.phases?.flatMap(p => p.tasks) || [];
  const completedTasks = allTasks.filter(t => t.status === 'completed').length;
  const totalPct = allTasks.length > 0 ? Math.round(completedTasks / allTasks.length * 100) : 0;

  return (
    <div className="space-y-6" data-testid="client-local-seo">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900">{t('localSeo.title')}</h2>
        <p className="text-sm text-gray-400">{t('localSeo.subtitle', { businessName: campaign.business_name, city: campaign.target_city })}</p>
      </div>

      {/* Overall Progress */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-gray-400">{t('localSeo.overallProgress')}</span>
          <span className="text-2xl font-black text-blue-400">{totalPct}%</span>
        </div>
        <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full transition-all duration-500" style={{ width: `${totalPct}%` }} />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          <span>{t('localSeo.tasksCompleted', { done: completedTasks, total: allTasks.length })}</span>
          <span>{t('localSeo.phaseOf', { current: campaign.current_phase })}</span>
        </div>
      </div>

      {/* Phase Progress Mini */}
      <div className="grid grid-cols-6 gap-2">
        {campaign.phases?.map((phase, idx) => {
          const Icon = PHASE_ICONS[idx] || Search;
          const color = PHASE_COLORS[idx];
          return (
            <div key={idx} className="text-center">
              <div className={`w-10 h-10 mx-auto rounded-lg flex items-center justify-center mb-1 ${phase.status === 'completed' ? 'bg-emerald-500/20' : phase.status === 'in_progress' ? `bg-opacity-20` : 'bg-gray-800'}`}
                style={phase.status === 'in_progress' ? { backgroundColor: `${color}20` } : {}}>
                {phase.status === 'completed'
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  : <Icon className="w-5 h-5" style={{ color: phase.status === 'in_progress' ? color : '#4b5563' }} />
                }
              </div>
              <div className="text-[10px] text-gray-500">P{phase.phase}</div>
            </div>
          );
        })}
      </div>

      {/* Phase Details */}
      <div className="space-y-2">
        {campaign.phases?.map((phase, idx) => {
          const Icon = PHASE_ICONS[idx] || Search;
          const color = PHASE_COLORS[idx];
          const isExpanded = expandedPhase === phase.phase;
          const pDone = phase.tasks?.filter(t => t.status === 'completed').length || 0;
          const pTotal = phase.tasks?.length || 0;
          const pPct = pTotal > 0 ? Math.round(pDone / pTotal * 100) : 0;

          return (
            <div key={phase.phase} className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden" data-testid={`client-phase-${phase.phase}`}>
              <div className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-100/40 transition-colors"
                onClick={() => setExpandedPhase(isExpanded ? null : phase.phase)}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}15` }}>
                  <Icon className="w-4 h-4" style={{ color }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900">{t('localSeo.phaseLabel', { n: phase.phase, name: phase.name })}</span>
                    {phase.status === 'completed' && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                    {phase.status === 'in_progress' && <Clock className="w-4 h-4" style={{ color }} />}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{phase.description}</p>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-bold" style={{ color }}>{pPct}%</div>
                  <div className="text-[10px] text-gray-500">{pDone}/{pTotal}</div>
                </div>
              </div>

              {isExpanded && (
                <div className="border-t border-gray-200 px-4 pb-4 pt-2 space-y-1">
                  {phase.tasks?.map(task => (
                    <div key={task.id} className="flex items-center gap-2 p-2 rounded-lg">
                      {task.status === 'completed'
                        ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                        : <Circle className="w-4 h-4 text-gray-600 shrink-0" />
                      }
                      <span className={`text-sm ${task.status === 'completed' ? 'text-gray-500 line-through' : 'text-gray-300'}`}>
                        {task.title}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
